import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import {
  ApplyPage,
  CompanyNotFoundPage,
  JobBoardPage,
  JobDetailsPage,
  Page404,
} from "pages";
import { CompanyHeader } from "components";
import { currentTenant, Tenant, TenantProvider } from "tenant";

// Links from before /job/:id existed.
const LegacyJobRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/job/${id ?? ""}`} replace />;
};

const LoadFailed = ({ retry }: { retry: () => void }) => (
  <main className="flex min-h-screen items-center justify-center bg-gray-100 p-6">
    <div role="alert" className="max-w-md rounded-2xl bg-white p-8 text-center">
      <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
      <p className="mt-4 text-gray-600">
        We couldn't load this careers page. Please try again.
      </p>
      <button
        type="button"
        onClick={retry}
        className="mt-6 rounded-lg bg-orange-500 px-4 py-2 font-medium text-white hover:bg-orange-600"
      >
        Try again
      </button>
    </div>
  </main>
);

type AppProps = { tenant?: Tenant };

/**
 * Path mode (jobs.redstonehr.com/acme/...) uses the slug as the router
 * basename, so the routes below serve /acme, /acme/job/:id, /acme/apply/:id.
 */
const App = ({ tenant = currentTenant() }: AppProps) => (
  <TenantProvider
    tenant={tenant}
    notFound={<CompanyNotFoundPage />}
    failed={(retry) => <LoadFailed retry={retry} />}
  >
    <BrowserRouter basename={tenant.basename || undefined}>
      <div className="min-h-screen bg-gray-100">
        <CompanyHeader />
        <main>
          <Routes>
            <Route path="/" element={<JobBoardPage />} />
            <Route path="/job/:id" element={<JobDetailsPage />} />
            <Route path="/apply/:id" element={<ApplyPage />} />
            <Route path="/job-details/:id" element={<LegacyJobRedirect />} />
            <Route path="*" element={<Page404 />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  </TenantProvider>
);

export default App;
